import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import PublicNav from "../components/PublicNav";


function Authenticity() {
  const nots = [
    {
      title: "No AI cover letters",
      desc: "We don't generate cover letters for you. Your voice is your strongest asset — we help you track and protect it, not replace it.",
    },
    {
      title: "No autofill or spray-and-pray",
      desc: "Joborigo is not a one-click apply tool. Mass applying to hundreds of jobs adds to the noise. We help you apply intentionally.",
    },
    {
      title: "No gaming the system",
      desc: "We don't optimize your resume to trick ATS algorithms. Real candidates with verified histories beat keyword stuffing every time.",
    },
    {
      title: "No fake signal",
      desc: "The hiring market is drowning in AI-generated applications. Joborigo is built on the opposite principle — real candidates, real history, no noise.",
    },
  ];

  return (
    <section className="py-20 bg-white dark:bg-brand-dark-bg">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
            Our philosophy
          </p>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
            Real candidates. Real history. No noise.
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
            AI flooded the top of the hiring funnel with noise. Joborigo sits
            below the funnel — helping you survive it with your authenticity
            intact.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {nots.map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-4 p-5 bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10"
            >
              <div className="w-8 h-8 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {item.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ThreeAudiences() {
  const audiences = [
    {
      eyebrow: "For agencies",
      title: "You own the process.",
      desc: "Create positions, build pipelines, share shortlists with clients, and push status to candidates. Joborigo never contacts your candidates directly or markets to them. Your relationships stay yours.",
    },
    {
      eyebrow: "For employers",
      title: "You see real candidates.",
      desc: "Connect with the agencies you already work with. Receive verified shortlists with real candidate histories. Give feedback per candidate in one place rather than scattered across recruiter inboxes.",
    },
    {
      eyebrow: "For job seekers",
      title: "You see honest status.",
      desc: "Track every application you submit, see status updates from the agency or employer handling each one, and stop wondering where applications went. Your voice and your history stay yours.",
    },
  ];

  return (
    <section className="py-20 bg-white dark:bg-brand-dark-bg">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
            Built for three sides
          </p>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
            One loop. Three parties. No black holes.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {audiences.map((a) => (
            <div
              key={a.eyebrow}
              className="p-6 bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10"
            >
              <p className="text-sm font-semibold text-brand-violet dark:text-brand-violet-on-dark mb-2">
                {a.eyebrow}
              </p>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {a.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {a.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TwoSidedPain() {
  const candidateStats = [
    { stat: "48%", label: "of job seekers were ghosted in 2025" },
    {
      stat: "77%",
      label: "of candidates want a way to track application status",
    },
    { stat: "28%", label: "have dropped out due to unclear communication" },
  ];

  const recruiterStats = [
    {
      stat: "37%",
      label:
        "of agency recruiters say too much manual work is their biggest operational barrier",
    },
    { stat: "17.7h", label: "spent per vacancy on manual admin tasks" },
    { stat: "45%", label: "increase in hiring touchpoints vs previous year" },
  ];

  return (
    <section className="bg-brand-violet dark:bg-brand-dark-surface py-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-semibold text-white mb-2">
            Recruitment coordination is broken on both sides.
          </h2>
          <p className="text-white/60 text-sm max-w-xl mx-auto">
            The fix isn't more tooling on one side. It's connecting both.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-4xl mx-auto">
          <div>
            <p className="text-xs font-semibold text-white/70 uppercase tracking-widest mb-6 text-center md:text-left">
              What candidates experience
            </p>
            <div className="space-y-6">
              {candidateStats.map((s) => (
                <div key={s.label} className="flex items-baseline gap-4">
                  <p className="text-4xl font-bold text-white flex-shrink-0 w-20">
                    {s.stat}
                  </p>
                  <p className="text-white/70 text-sm leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-white/70 uppercase tracking-widest mb-6 text-center md:text-left">
              What recruiters experience
            </p>
            <div className="space-y-6">
              {recruiterStats.map((s) => (
                <div key={s.label} className="flex items-baseline gap-4">
                  <p className="text-4xl font-bold text-white flex-shrink-0 w-20">
                    {s.stat}
                  </p>
                  <p className="text-white/70 text-sm leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      {/* Nav */}
      <PublicNav />

      {/* Hero */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-6 py-24 bg-brand-light-bg dark:bg-brand-dark-bg">
        <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4 max-w-3xl leading-tight">
          Recruitment, with the loop closed.
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-lg mb-2 max-w-2xl mx-auto">
          Joborigo is recruitment transparency software for agencies, employers,
          and job seekers.
        </p>
        <p class="text-gray-500 dark:text-gray-400 text-lg mb-2 max-w-2xl mx-auto">
          Every application gets a real status. Every status flows to every
          party that needs it. Nobody gets ghosted because the platform forgot
          they existed.
        </p>
        <p class="text-gray-500 dark:text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
          Real-time status updates flow between candidates, agencies, and employers, so every party sees exactly where each application stands.
        </p>
        <Link
          to="/register"
          className="bg-brand-violet text-white px-8 py-3 rounded-lg text-lg font-medium hover:opacity-90 transition"
        >
          Get started for free
        </Link>
      </section>

      {/* Three audiences */}
      <ThreeAudiences />

      {/* Two-sided pain */}
      <TwoSidedPain />

      {/* Authenticity */}
      <Authenticity />

      {/* Footer */}
      <Footer />
    </div>
  );
}
